import { ApplicationConfig, importProvidersFrom, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { MAT_DATE_LOCALE, provideNativeDateAdapter } from '@angular/material/core';
import { TranslateModule, provideTranslateLoader } from '@ngx-translate/core';
import { routes } from './app.routes';
import { authInterceptor } from './core/auth/auth.interceptor';
import { serviceAvailabilityInterceptor } from './core/service-status/service-availability.interceptor';
import { AuthStore } from './core/auth/auth.store';
import { BackendTranslateLoader } from './core/i18n/backend-translate.loader';
import { TranslationStore } from './core/services/translation.service';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAnimationsAsync(),
    // Material date/time pickers need a date adapter. The native adapter works in
    // browser-local time; app-datetime-field converts Date <-> string using local
    // components only, so nothing round-trips through UTC. Force ro-RO so the
    // datepicker renders dd.MM.yyyy / 24h to match the Romanian market.
    provideNativeDateAdapter(),
    { provide: MAT_DATE_LOCALE, useValue: 'ro-RO' },
    provideRouter(routes),
    // Availability last, so a 401 that the auth interceptor retries with a refreshed token is
    // judged on the retry's outcome rather than on the attempt that provoked the refresh.
    provideHttpClient(withXhr(), withInterceptors([authInterceptor, serviceAvailabilityInterceptor])),
    // Flags must be chained onto the session restore, not registered as a second
    // initializer: Angular starts initializer factories concurrently, and the access
    // token may only exist after tryRestoreSession's async refresh completes.
    provideAppInitializer(async () => {
      const authStore = inject(AuthStore);
      await authStore.tryRestoreSession();
      await authStore.loadFeatureFlags();
    }),
    // The supported-language list is anonymous, so it can load independently of auth.
    // On failure the store keeps its cached/bundled list, so this never blocks boot.
    provideAppInitializer(async () => {
      await inject(TranslationStore).loadLanguages();
    }),
    importProvidersFrom(
      TranslateModule.forRoot({
        defaultLanguage: 'en'
      })
    ),
    // Strings come from the backend (admin bundle) with a bundled-file fallback.
    provideTranslateLoader(BackendTranslateLoader)
  ]
};
